import React from 'react';
import { FaGithub, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';
import { Mail } from 'lucide-react';
import developer from '../../assets/images/developer.webp';
import { Container, Eyebrow, PillButton, navyIn, NAVY_SECTION, Fade, NAVY, LIGHT } from '../../components/home/ui';
import Faq from '../../components/home/Faq';

const COMPANY_URL = 'https://binudsoftwaresolutions.in';

const socials = [
  { label: 'LinkedIn', href: 'https://linkedin.com/in/binudpanging', icon: <FaLinkedinIn className="w-4 h-4" /> },
  { label: 'Twitter', href: 'https://x.com/binudp_dev', icon: <FaXTwitter className="w-4 h-4" /> },
  { label: 'GitHub', href: 'https://github.com/devBinud', icon: <FaGithub className="w-4 h-4" /> },
  { label: 'Email', href: 'mailto:binudp.dev@gmail.com', icon: <Mail className="w-4 h-4" /> },
];

const metrics = [
  { value: '7+', label: 'Years\nExperience' },
  { value: '43+', label: 'Web & Mobile\nProjects Shipped' },
  { value: '24+', label: 'Startups & SMEs\nPartnered With' },
  { value: '100%', label: 'Sprint-Driven\nDelivery Success' },
];

const pillars = ['Full-Stack Architecture', 'Cloud APIs & Microservices', 'Cross-Platform Mobile Apps', 'Database Optimization'];

const steps = [
  {
    num: '01',
    title: 'Architecture & Schema Modeling',
    desc: 'Deep technical discovery, modular system design, and database schema planning before a line of code ships.',
  },
  {
    num: '02',
    title: 'Iterative Sprint Development',
    desc: 'Rapid frontend and backend execution with continuous testing and weekly milestones you can see.',
  },
  {
    num: '03',
    title: 'Production Deployment & Scale',
    desc: 'CI/CD rollout, performance benchmarks, and automated observability so the product keeps running.',
  },
];

const About = () => (
  <>
    {/* ── Hero ── */}
    <section className="relative overflow-hidden bg-linear-to-b from-[#d5d9e3] to-[#c8cdd8] pt-28 lg:pt-32">
      <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
        <div className="lg:col-span-7 pb-4 lg:pb-20">
          <Eyebrow>About Me</Eyebrow>
          <h1 className="mt-4 text-[40px] sm:text-[54px] lg:text-[62px] font-medium leading-[1.02] tracking-[-0.045em] text-[#111]">
            Architecting
            <br />
            High-Performance
            <br />
            Systems &amp; Products
          </h1>
          <p className="mt-6 text-[15px] sm:text-base text-[#111]/75">
            Founder of{' '}
            <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer" className="text-[#111] font-medium underline underline-offset-4 decoration-[#3b5bfd]">
              Binud Software Solutions
            </a>{' '}
            &amp; Full-Stack Systems Architect
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PillButton to="/contact">Start a Project</PillButton>
            <PillButton href={COMPANY_URL} variant="light">
              Company Site
            </PillButton>
          </div>
        </div>

        <div className="lg:col-span-5 relative mx-auto w-[80%] max-w-[400px] lg:w-full">
          <div className="relative aspect-[41/52] rounded-t-full overflow-hidden [mask-image:linear-gradient(to_bottom,black_62%,transparent_96%)]">
            <img src={developer} alt="Binud Panging" className="w-full h-full object-cover object-[50%_18%]" />
            <div className="absolute inset-x-0 bottom-0 h-[45%] bg-linear-to-b from-transparent to-[#cdd2dc]" />
          </div>
          <div className="absolute -left-6 sm:-left-12 bottom-16 flex items-center gap-5 rounded-2xl px-5 py-4 bg-white/85 backdrop-blur-md shadow-[0_12px_30px_rgba(17,24,58,0.08)]">
            <span className="text-[13px] leading-snug text-[#111]/75">
              Binud Panging
              <br />
              <span className="text-[#111]/50">Guwahati, Assam</span>
            </span>
            <img src={process.env.PUBLIC_URL + '/logo-on-dark.png'} alt="" className="w-9 h-9" />
          </div>
        </div>
      </Container>
    </section>

    {/* ── Story + metrics ── */}
    <section className="bg-[#e9ecf2] pt-20 pb-12 lg:pt-28 lg:pb-16">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <div className="lg:col-span-4">
            <Eyebrow>My Story</Eyebrow>
          </div>
          <div className="lg:col-span-8">
            <p className="text-[28px] sm:text-[36px] lg:text-[40px] font-medium leading-[1.12] tracking-[-0.035em] text-[#111]">
              A self-taught engineer, driven by curiosity for how systems operate at scale.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-[15px] leading-relaxed text-[#111]/70">
              <p>
                Over the past 7+ years of hands-on production engineering, I have architected resilient cloud backends,
                high-performance web applications, and fluid cross-platform mobile experiences for startups and scaling
                businesses globally.
              </p>
              <p>
                Through <span className="text-[#111] font-medium">Binud Software Solutions</span>, I provide end-to-end
                technical leadership - transforming complex engineering requirements into robust, secure, and
                revenue-driving digital products.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.map((m) => (
            <div key={m.value} className="rounded-2xl bg-[#f4f6fa] p-5 sm:p-6">
              <p className="text-3xl sm:text-4xl font-medium tracking-tight text-[#111]">
                {m.value}
              </p>
              <p className="mt-3 text-[13px] leading-snug text-[#111]/65 whitespace-pre-line">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-3 rounded-2xl bg-[#f4f6fa] p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <p className="text-xl font-medium tracking-[-0.02em] text-[#111]">Core Engineering Pillars</p>
          <div className="flex flex-wrap gap-2">
            {pillars.map((p) => (
              <span key={p} className="bg-[#e6e9f0] rounded-full px-4 py-1.5 text-sm text-[#111]/75">
                {p}
              </span>
            ))}
          </div>
        </div>
      </Container>
    </section>

    {/* ── How I work (navy) ── */}
    <section>
      <div className={NAVY_SECTION} style={navyIn}>
        <Container>
          <div className="text-center">
            <Eyebrow dark>How I Work</Eyebrow>
            <h2 className="mt-3 text-[34px] sm:text-[44px] lg:text-[48px] font-medium leading-[1.05] tracking-[-0.04em] text-white">
              An agile delivery blueprint
              <br />
              for every build
            </h2>
          </div>

          <div className="mt-12 rounded-[28px] bg-white/[0.06] border border-white/10 p-3 sm:p-5 grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 shadow-[0_30px_80px_rgba(0,0,0,0.25)]">
            {/* Founder card */}
            <div className="rounded-2xl p-6 flex flex-col bg-[radial-gradient(120%_90%_at_25%_0%,#3b5bfd_0%,#6f86ff_40%,#c9d2ff_75%,#eef1fb_100%)] min-h-[340px] lg:row-span-3">
              <img src={process.env.PUBLIC_URL + '/logo-on-dark.png'} alt="Binud Software Solutions logo" className="w-14 h-14" />
              <h3 className="mt-6 text-2xl sm:text-[26px] font-medium tracking-[-0.03em] text-white">Binud Software Solutions</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/85 max-w-[280px]">
                The studio I founded to build websites, apps, and business software for companies across Assam and beyond.
              </p>
              <div className="mt-auto pt-8 flex flex-wrap items-center gap-3">
                <PillButton href={COMPANY_URL}>Visit Company</PillButton>
              </div>
            </div>

            {/* Steps */}
            <div className="lg:col-span-2 grid grid-cols-1 gap-3 sm:gap-4">
              {steps.map((s) => (
                <div key={s.num} className="rounded-2xl bg-[#f4f6fa] p-5 sm:p-6 flex items-start gap-5">
                  <span className="text-[40px] leading-none font-semibold tracking-tight text-[#111]/[0.12] shrink-0">{s.num}</span>
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium tracking-[-0.02em] text-[#111]">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#111]/65">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-5">
            <p className="text-white/80 text-sm">Find me online</p>
            <div className="flex items-center gap-2.5">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-11 h-11 rounded-full bg-[#eef1fb] text-[#111] flex items-center justify-center transition-transform hover:-translate-y-0.5"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </Container>
      </div>
      <Fade from={NAVY} to={LIGHT} className="h-28 lg:h-56" />
    </section>

    <Faq />
  </>
);

export default About;
